import type {
  ReleaseProfileFormula,
  ReleaseProfileFormulaItem,
  ReleaseProfilesAndVariables,
  SubFormulaBrief,
} from '../../types/release-profiles.ts';
import type { EmissionsComplianceMetricConfig } from '../../types/emissions.ts';
import { VariableNameFinder } from '../formulas/variable-name-finder.ts';
import { VariableNameReplacer } from '../formulas/variable-name-replacer.ts';
import { Tokenizer } from '../formulas/tokenizer.ts';
import { Parser } from '../formulas/parser.ts';
import { ExpressionTypeChecker, type TypeCheckResultBase } from '../formulas/expression-type-checker.ts';
import {
  formulaPredefinedVariables,
  notApplicableVar,
  productChemicalConcentrationVar,
  productElementalConcentrationVar,
  variablesImplyProduct,
} from '../formulas/formula-utils.ts';
import { UnitsTracker } from '../formulas/units.ts';
import { unitStringAnalyze, type UnitRestriction } from '../formulas/unit-catalog.ts';

function complianceMetricReferencedVariableNames(formulaObj: ReleaseProfileFormula): Set<string> {
  const formulasByVariableName = new Map(
    Object.values(formulaObj.formulas).map((formula) => [formula.nameForVariable, formula]),
  );
  const names = new Set<string>();
  const visit = (formula: ReleaseProfileFormulaItem) => {
    const found = VariableNameFinder.findInString(formula.formula);
    if (found.status === 'error') return;
    for (const name of found.variableNames) {
      if (names.has(name)) continue;
      names.add(name);
      const internalFormula = formulasByVariableName.get(name);
      if (internalFormula) visit(internalFormula);
    }
  };
  for (const formulaItem of Object.values(formulaObj.formulas)) {
    visit(formulaItem);
  }
  return names;
}

export function complianceMetricMixCalculationVariableNames(
  formulaObj: ReleaseProfileFormula,
  profiles: Pick<ReleaseProfilesAndVariables, 'mixCalculations'>,
): Set<string> {
  const referencedVariableNames = complianceMetricReferencedVariableNames(formulaObj);
  return new Set(
    profiles.mixCalculations.map((mix) => mix.nameForVariable).filter((name) => referencedVariableNames.has(name)),
  );
}

/** Validates that a metric references only values its emissions evaluator can provide. */
export function complianceMetricDependencyValidationError(
  formulaObj: ReleaseProfileFormula,
  profiles: Pick<ReleaseProfilesAndVariables, 'constants' | 'questions' | 'chemicalProfiles' | 'mixCalculations'>,
): string | null {
  const mixCalculationVariableNames = complianceMetricMixCalculationVariableNames(formulaObj, profiles);
  const referencedMixCalculation = profiles.mixCalculations.find((mix) =>
    mixCalculationVariableNames.has(mix.nameForVariable),
  );
  if (referencedMixCalculation) {
    return `Compliance metrics cannot use mix calculation "${referencedMixCalculation.nameFriendly}".`;
  }
  const validChoiceLists = formulaObj.choiceLists;
  for (const formula of Object.values(formulaObj.formulas)) {
    for (const output of Object.values(formula.outputs)) {
      for (const condition of output.ifChoices) {
        const choiceList = validChoiceLists[condition.choiceListID];
        if (!choiceList?.choices.some((choice) => choice.choiceID === condition.choiceID)) {
          return 'A compliance metric condition references a missing question or answer.';
        }
      }
    }
    if (formula.formula.trim() === '') continue;
    const found = VariableNameFinder.findInString(formula.formula);
    if (found.status === 'error') return found.error;
    const supportedVariableNames = new Set([
      ...formulaPredefinedVariables.map((variable) => variable.nameForVariable),
      ...profiles.constants.map((variable) => variable.nameForVariable),
      ...profiles.questions.map((variable) => variable.nameForVariable),
      ...profiles.chemicalProfiles.map((variable) => variable.nameForVariable),
      ...Object.values(formulaObj.formulas).map((candidate) => candidate.nameForVariable),
    ]);
    const unsupported = Array.from(found.variableNames).find((name) => !supportedVariableNames.has(name));
    if (unsupported) return `Compliance metrics cannot use the unsupported variable "${unsupported}".`;
  }
  return null;
}

/** A limit measured from unit emissions calculations (actual or potential) caps a mass of emissions. */
export const emissionsLimitMassUnitRestriction: UnitRestriction = { kind: 'convertibleTo', unit: 'lbs', label: 'mass' };

/**
 * The units a limit may be compared in. A limit without a compliance metric (`config` is null) must be a mass, and a
 * metric's limit must convert to the metric's expected unit, which is filled in from its formulas when the metric is
 * saved (see `complianceMetricExpectedUnitsResolve`).
 */
export function emissionsLimitUnitRestriction(config: EmissionsComplianceMetricConfig | null): UnitRestriction {
  if (!config) {
    return emissionsLimitMassUnitRestriction;
  }

  // A metric that names no expected unit gives us nothing to check the entry against, so any unit we understand is
  // accepted; the compliance evaluation skips formula outputs it cannot convert to the limit's unit.
  const expected = config.expectedUnits?.value.trim();
  if (!expected || UnitsTracker.fromParsedUnitString(expected).isUnknown()) {
    return { kind: 'any', allowCustom: false };
  }
  return { kind: 'convertibleTo', unit: expected, label: null };
}

/**
 * Validates the comparison unit entered on a limit, which is free text rather than a choice from a list of convertible
 * units: that list is expensive to build and can never be complete, because a unit has several spellings ('lb' and
 * 'lbs' name the same unit) and unit products multiply out combinatorially. Returns `null` when the entry is usable,
 * and an explanation to show the user otherwise.
 */
export function emissionsLimitUnitError(
  limitUnit: string,
  config: EmissionsComplianceMetricConfig | null,
): string | null {
  const expected = config?.expectedUnits?.value.trim();
  if (expected && UnitsTracker.fromParsedUnitString(expected).isUnknown()) {
    return `This compliance metric expects units of "${expected}", which this system does not understand.`;
  }

  const analysis = unitStringAnalyze(limitUnit, emissionsLimitUnitRestriction(config));
  if (analysis.status === 'empty') {
    return 'Enter a comparison unit.';
  } else if (analysis.status === 'ok') {
    return null;
  }
  return analysis.message;
}

type ComplianceMetricFormulaTypeCheck =
  | { status: 'ok'; units: UnitsTracker }
  /** Made of unit names we don't understand, so no limit can convert it. */
  | { status: 'unknown'; units: UnitsTracker }
  | { status: 'error'; error: string };

export type ComplianceMetricFormulaUnits = {
  nameFriendly: string;
  /** Formulas without a numerator or denominator output aren't part of what a limit on the metric measures. */
  role: 'numerator' | 'denominator';
} & ComplianceMetricFormulaTypeCheck;

/**
 * The units of each of a metric's numerator and denominator formulas, worked out from the units declared on the
 * variables they use rather than by evaluating them, so that no facility data is needed. Formula references are
 * expanded the same way the evaluator expands them.
 */
export function complianceMetricFormulaUnits(
  formulaObj: ReleaseProfileFormula,
  variables: readonly { nameForVariable: string; units: string }[],
): ComplianceMetricFormulaUnits[] {
  const declaredUnits = new Map<string, string>();
  for (const variable of [...formulaPredefinedVariables, ...variables]) {
    declaredUnits.set(variable.nameForVariable, variable.units);
  }
  // Described to users as %, but evaluated as a unitless fraction of the product (see ProductMassFraction):
  declaredUnits.set(productChemicalConcentrationVar, '');
  declaredUnits.set(productElementalConcentrationVar, '');
  // Annotating a reference doesn't change its units, so every variable can be treated as annotatable:
  const annotatableVariableNames = new Set(declaredUnits.keys());

  const results: ComplianceMetricFormulaUnits[] = [];
  for (const formula of Object.values(formulaObj.formulas)) {
    const outputTypes = new Set(Object.values(formula.outputs).map((output) => output.type));
    const roles: ComplianceMetricFormulaUnits['role'][] = [];
    if (outputTypes.has('complianceNumerator')) roles.push('numerator');
    if (outputTypes.has('complianceDenominator')) roles.push('denominator');
    const trimmed = formula.formula.trim();
    // N/A, or a formula yet to be written, produces nothing:
    if (roles.length === 0 || trimmed === '' || trimmed === notApplicableVar) {
      continue;
    }

    const checked = complianceMetricFormulaTypeCheck(formula, formulaObj, declaredUnits, annotatableVariableNames);
    for (const role of roles) {
      results.push({ nameFriendly: formula.nameFriendly, role, ...checked });
    }
  }

  return results;
}

function complianceMetricFormulaTypeCheck(
  formula: ReleaseProfileFormulaItem,
  formulaObj: ReleaseProfileFormula,
  declaredUnits: Map<string, string>,
  annotatableVariableNames: Set<string>,
): ComplianceMetricFormulaTypeCheck {
  const otherFormulas = new Map<string, SubFormulaBrief>();
  for (const otherFormula of Object.values(formulaObj.formulas)) {
    if (otherFormula !== formula) {
      otherFormulas.set(otherFormula.nameForVariable, otherFormula);
    }
  }
  const { formulaNew } = VariableNameReplacer.substituteFormulasIgnoringErrors(
    formula,
    otherFormulas,
    annotatableVariableNames,
  );

  const tokenized = Tokenizer.tokenize(formulaNew);
  if ('error' in tokenized) {
    return { status: 'error', error: tokenized.error };
  }
  const parsed = Parser.parseExpression(tokenized.tokens);
  if ('error' in parsed) {
    return { status: 'error', error: parsed.error };
  }

  const variableTypes = new Map<string, TypeCheckResultBase>();
  for (const identifier of VariableNameFinder.findIdentifiersInExpression(parsed.expression)) {
    const units = declaredUnits.get(identifier.nameBare);
    // Anything missing is reported by the type checker as an unknown identifier:
    if (units !== undefined) {
      variableTypes.set(identifier.nameWithAnnotation, {
        type: 'number',
        units: UnitsTracker.fromParsedUnitString(units),
      });
    }
  }

  const checked = new ExpressionTypeChecker(parsed.expression, variableTypes).typeCheck();
  if (checked.type === 'error') {
    return { status: 'error', error: checked.error };
  } else if (checked.type !== 'number') {
    return { status: 'error', error: `It produces a ${checked.type} rather than a number.` };
  }
  return { status: checked.units.isUnknown() ? 'unknown' : 'ok', units: checked.units };
}

function complianceMetricFormulaUnitsDescribe(formulaUnits: ComplianceMetricFormulaUnits[]): string {
  return formulaUnits
    .map((f) => ('units' in f ? `${f.nameFriendly} (${f.units.toString() || 'no units'})` : f.nameFriendly))
    .join(', ');
}

/** The single unit one side of a ratio is added up in, as every line on that side is converted to the first. */
function complianceMetricRatioSideUnits(
  side: ComplianceMetricFormulaUnits['role'],
  formulaUnits: ComplianceMetricFormulaUnits[],
): { units: UnitsTracker } | { error: string } {
  const [first, ...rest] = formulaUnits;
  if (
    first?.status === 'ok' &&
    rest.every((f) => f.status === 'ok' && typeof f.units.convertTo(first.units, 1) === 'number')
  ) {
    return { units: first.units };
  }
  return {
    error:
      `The ${side} of this compliance metric is added up before dividing, so its formulas must all produce units ` +
      `this system understands that convert to each other: ${complianceMetricFormulaUnitsDescribe(formulaUnits)}.`,
  };
}

export type ComplianceMetricProducedUnits =
  /** No numerator has been written yet. */
  | { status: 'unwritten' }
  /**
   * Without a denominator, each numerator's units, as a limit measures whichever of them convert to its unit. With
   * one, the single unit of the numerator's total divided by the denominator's.
   */
  | { status: 'ok'; units: UnitsTracker[]; formulasDescribed: string }
  | { status: 'error'; message: string };

/** The units a metric's values can come out in, which its limits are compared against. */
export function complianceMetricProducedUnits(
  formulaObj: ReleaseProfileFormula,
  variables: readonly { nameForVariable: string; units: string }[],
): ComplianceMetricProducedUnits {
  const formulaUnits = complianceMetricFormulaUnits(formulaObj, variables);
  const numerators = formulaUnits.filter((f) => f.role === 'numerator');
  const denominators = formulaUnits.filter((f) => f.role === 'denominator');
  if (numerators.length === 0) {
    return { status: 'unwritten' };
  }
  const failed = formulaUnits.find((f) => f.status === 'error');
  if (failed) {
    return {
      status: 'error',
      message: `The units of the formula "${failed.nameFriendly}" are unclear: ${failed.error}`,
    };
  }

  const formulasDescribed = complianceMetricFormulaUnitsDescribe(numerators);
  if (denominators.length === 0) {
    const units = numerators.flatMap((f) => (f.status === 'ok' ? [f.units] : []));
    if (units.length === 0) {
      return {
        status: 'error',
        message:
          "None of this compliance metric's formulas produce units this system understands: " +
          `${formulasDescribed}. Check the units of the variables they use.`,
      };
    }
    return { status: 'ok', units, formulasDescribed };
  }

  const numerator = complianceMetricRatioSideUnits('numerator', numerators);
  if ('error' in numerator) {
    return { status: 'error', message: numerator.error };
  }
  const denominator = complianceMetricRatioSideUnits('denominator', denominators);
  if ('error' in denominator) {
    return { status: 'error', message: denominator.error };
  }
  return { status: 'ok', units: [numerator.units.divide(denominator.units)], formulasDescribed };
}

/**
 * The units a metric's expected unit may be chosen from: those its single produced unit converts to, or, when its
 * formulas produce several unrelated units, any unit, with what each formula produces recommended.
 */
export function complianceMetricExpectedUnitsOptions(produced: ComplianceMetricProducedUnits): {
  restriction: UnitRestriction;
  recommended: string[];
} {
  if (produced.status !== 'ok') {
    return { restriction: { kind: 'any', allowCustom: false }, recommended: [] };
  }

  const recommended = Array.from(new Set(produced.units.map((units) => units.toString()))).filter((s) => s !== '');
  const [first, ...rest] = produced.units;
  const single = rest.every((units) => typeof units.convertTo(first, 1) === 'number');
  // A unitless value has no spelling to restrict by:
  if (single && first.toString() !== '') {
    return { restriction: { kind: 'convertibleTo', unit: first.toString(), label: null }, recommended };
  }
  return { restriction: { kind: 'any', allowCustom: false }, recommended };
}

/**
 * Checks a metric's expected units against what its formulas produce, filling them in when blank so that limits on
 * the metric can be restricted to units that work. Without a denominator, an entered unit only needs to convert from
 * one numerator formula, as the compliance evaluation skips outputs it cannot convert to the limit's unit. With one,
 * the metric measures the numerator's total divided by the denominator's.
 */
export function complianceMetricExpectedUnitsResolve(
  config: EmissionsComplianceMetricConfig,
  formulaObj: ReleaseProfileFormula,
  variables: readonly { nameForVariable: string; units: string }[],
): { status: 'ok'; config: EmissionsComplianceMetricConfig } | { status: 'error'; message: string } {
  const producedUnits = complianceMetricProducedUnits(formulaObj, variables);
  // Nothing has been written yet that could contradict the expected unit, or fill it in:
  if (producedUnits.status === 'unwritten') {
    return { status: 'ok', config };
  } else if (producedUnits.status === 'error') {
    return producedUnits;
  }
  const produced = producedUnits.units;

  const expected = config.expectedUnits?.value.trim() ?? '';
  if (expected === '') {
    const [first, ...rest] = produced;
    if (rest.some((units) => typeof units.convertTo(first, 1) !== 'number')) {
      return {
        status: 'error',
        message:
          "This compliance metric's formulas produce units that cannot be converted to each other: " +
          `${producedUnits.formulasDescribed}. Enter the comparison unit its limits should use.`,
      };
    }
    return {
      status: 'ok',
      config: {
        ...config,
        expectedUnits: { value: first.toString(), mandatory: config.expectedUnits?.mandatory ?? false },
      },
    };
  }

  const expectedUnits = UnitsTracker.fromParsedUnitString(expected);
  if (expectedUnits.isUnknown()) {
    return { status: 'error', message: `The comparison unit "${expected}" is not a unit this system understands.` };
  } else if (!produced.some((units) => typeof units.convertTo(expectedUnits, 1) === 'number')) {
    const producedDescribed = produced.map((units) => units.toString() || 'no units').join(', ');
    return {
      status: 'error',
      message: `The comparison unit ${expected} cannot be converted from what this compliance metric produces: ${producedDescribed}.`,
    };
  }
  return { status: 'ok', config };
}

/** Product-dependent metrics require product assignments on every selected source. */
export function complianceMetricRequiresProducts(formulaObj: ReleaseProfileFormula): boolean {
  return variablesImplyProduct(complianceMetricReferencedVariableNames(formulaObj));
}
